import classNames from 'classnames';
import styles from './Pagination.module.scss';

function getPageNumbers(currentPage: number, totalPages: number): (number | string)[] {
  const pages: (number | string)[] = [];

  if (totalPages <= 5) {
    // If there are only 5 or fewer pages, show all
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
    return pages;
  }

  // Always include first page
  pages.push(1);

  // Show left ellipsis if needed
  if (currentPage > 3) {
    pages.push('...');
  }

  // Middle pages
  const startPage = Math.max(2, currentPage - 1);
  const endPage = Math.min(totalPages - 1, currentPage + 1);

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  // Show right ellipsis if needed
  if (currentPage < totalPages - 2) {
    pages.push('...');
  }

  // Always include last page
  pages.push(totalPages);

  return pages;
}

export default function Pagination({
  currentPage,
  handleClick,
  totalPages,
  totalResults,
  currentResultsCount,
}: {
  currentPage: number;
  handleClick: (pageNumber: number) => void;
  totalPages: number;
  totalResults: number;
  currentResultsCount: number;
}) {
  const pageNumbers = getPageNumbers(currentPage, totalPages);

  return (
    <div className={styles.pagination}>
      <div className={styles.links}>
        <button
          className={classNames({ [styles.inactive]: currentPage === 1 })}
          disabled={currentPage === 1}
          onClick={() => (currentPage > 1 ? handleClick(currentPage - 1) : null)}
        >
          &lt; Previous
        </button>
        {pageNumbers.map((page, idx) =>
          typeof page === 'string' ? (
            <span key={`ellipsis-${idx}`}>{page}</span>
          ) : (
            <button
              key={`page-${page}`}
              className={classNames({ [styles.active]: currentPage === page })}
              onClick={() => handleClick(page)}
            >
              {page}
            </button>
          ),
        )}
        <button
          className={classNames({ [styles.inactive]: currentPage === totalPages })}
          disabled={currentPage === totalPages}
          onClick={() => (currentPage < totalPages ? handleClick(currentPage + 1) : null)}
        >
          Next &gt;
        </button>
      </div>
      <div className={styles.info}>
        Showing {currentResultsCount} of {totalResults} results
      </div>
    </div>
  );
}
